import React from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';

import { useSelector } from 'hooks';
import { toUrlSlug } from 'utils/stepUrlUtils';
import { getFollowingStep } from 'utils/stepUtils';
import { getWorkflowsForAllDealers } from 'modules/dealers/selectors';
import { SelfServiceType } from 'modules/selfServices/types/SelfService';

import { LOGIN_STEPS, LoginStep } from './Steps';
import type { LoginOutletContext } from './LoginStepContent';

const LoginLayout: React.FC = () => {
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const { step: stepSlug } = useParams<{ step: string }>();
  const workflows = useSelector(getWorkflowsForAllDealers);

  const steps: LoginStep[] = React.useMemo(() => {
    const visibleSteps = LOGIN_STEPS.filter(({ data }) => (
      typeof data.isVisible === 'boolean' ? data.isVisible : data.isVisible({ type: state?.type as SelfServiceType, workflows })
    ));

    return visibleSteps.map((step) => ({
      ...step,
      next: getFollowingStep<LoginStep>(step, visibleSteps, LOGIN_STEPS),
      previous: getFollowingStep<LoginStep>(step, visibleSteps, LOGIN_STEPS, 'previous'),
    }));
  }, [state?.type, workflows]);

  // Redirect to first step if no step in URL
  React.useEffect(() => {
    if (steps.length > 0 && !stepSlug) {
      navigate(`${toUrlSlug(steps[0].key)}${search}`, {
        replace: true,
        state,
      });
    }
  }, [steps, stepSlug, navigate, search, state]);

  // Reset flow when steps change (e.g., switching from CHECKIN to BREAKDOWN)
  const stepsSignature = React.useMemo(() => steps.map((s) => s.key).join('|'), [steps]);

  React.useEffect(() => {
    if (steps.length > 0 && stepSlug) {
      const currentStepExists = steps.some((s) => toUrlSlug(s.key) === stepSlug);
      if (!currentStepExists) {
        navigate(`${toUrlSlug(steps[0].key)}${search}`, {
          replace: true,
          state,
        });
      }
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [stepsSignature]);

  const outletContext: LoginOutletContext = { steps };

  return (
    <div>
      <Outlet context={outletContext} />
    </div>
  );
};

export default LoginLayout;
